// The only way Seedbed modifies an Audiotool document.
//
// SyncedDocument.modify() is `const t = await createTransaction(); const r =
// await fn(t); t.send();` — no try/finally. ANY throw in the callback, a
// validation failure or a plain bug, leaves the document lock held forever,
// and every later modify() waits on it silently (spike D1). So Seedbed never
// calls modify(): it owns the builder and always sends (D2).
//
// Validation throws at create()/update() time and leaves the builder usable
// (D3), so callers that apply many independent ops catch per op and let the
// rest of the batch commit — see write.ts.
//
// Acquiring the lock is bounded too: if something ELSE holds it (an SDK path
// that hit D1), waiting forever would wedge the bridge with no error at all.
// After `lockTimeoutMs` it throws DocumentLockTimeout and the caller reopens
// the document.

import type { OfflineDocument, SyncedDocument } from "@audiotool/nexus";
import type { TransactionBuilder } from "@audiotool/nexus/document";

/** What the writer and reader need of a document — offline or synced. */
export type NexusDoc = Pick<SyncedDocument | OfflineDocument, "createTransaction" | "queryEntities">;

export class DocumentLockTimeout extends Error {
  constructor(ms: number) {
    super(`The Audiotool document stayed locked for ${Math.round(ms / 1000)} s; it needs reopening.`);
    this.name = "DocumentLockTimeout";
  }
}

export interface TransactOptions {
  lockTimeoutMs?: number;
}

export async function transact<T>(
  doc: NexusDoc,
  fn: (t: TransactionBuilder) => T,
  opts: TransactOptions = {},
): Promise<T> {
  const ms = opts.lockTimeoutMs ?? 30_000;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const pending = doc.createTransaction();
  let t: TransactionBuilder;
  try {
    t = await Promise.race([
      pending,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new DocumentLockTimeout(ms)), ms);
      }),
    ]);
  } catch (e) {
    // If the lock is granted after we gave up, hand it straight back.
    void pending.then((late) => late.send()).catch(() => {});
    throw e;
  } finally {
    clearTimeout(timer);
  }
  try {
    return fn(t);
  } finally {
    try {
      t.send();
    } catch (e) {
      console.warn("[Audiotool] send() threw; the lock is released regardless:", e);
    }
  }
}
